/**
 * usePermissions — Iter63a
 *
 * Hook React qui récupère et cache 60s la matrice de permissions
 * d'un user pour un chantier donné. Source : GET /api/chantiers/{cid}/my-permissions.
 *
 * Cache module-scoped (partagé entre composants), TTL 60s par chantier_id.
 *
 * Usage :
 *   const { perms, role_projet, isOwner, loading, error, refresh } = usePermissions(chantierId);
 *   if (perms.can_modify_team) { ... }
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TTL_MS = 60_000;

// Cache global { [chantierId]: { fetchedAt, payload } }
const cache = new Map();

const DEFAULT_PERMS = {
  can_modify_team: false,
  can_modify_planning: false,
  can_modify_chantier_settings: false,
  can_delete_chantier: false,
  can_validate_documents: false,
  can_see_finances: false,
  can_see_marges: false,
  can_see_other_companies: false,
  can_see_internal_communications: false,
  can_see_recent_activity: false,
  can_see_quick_actions: false,
  can_create_crc: false,
  can_see_crc_drafts: false,
  // Iter63c-A
  can_modify_dpgf: false,
  can_modify_cctp: false,
  can_modify_visite: false,
  // RP17-19 / RP34 — droits « engager » (D2)
  can_diffuser_crc: false,
  can_lock_dpgf: false,
  // RP36 — lancer / attribuer une consultation (droit cabinet délégable)
  can_manage_consultation: false,
};

export function clearPermissionsCache(chantierId) {
  if (chantierId) cache.delete(chantierId);
  else cache.clear();
}

export function usePermissions(chantierId) {
  const [state, setState] = useState({
    perms: DEFAULT_PERMS,
    role_projet: null,
    isOwner: false,
    loading: !!chantierId,
    error: null,
  });
  const aliveRef = useRef(true);

  const load = useCallback(
    async (force = false) => {
      if (!chantierId) {
        setState({ perms: DEFAULT_PERMS, role_projet: null, isOwner: false, loading: false, error: null });
        return;
      }
      const now = Date.now();
      const hit = cache.get(chantierId);
      if (!force && hit && now - hit.fetchedAt < TTL_MS) {
        if (aliveRef.current) {
          setState({
            perms: { ...DEFAULT_PERMS, ...(hit.payload.permissions || {}) },
            role_projet: hit.payload.role_projet || null,
            isOwner: !!hit.payload.is_owner,
            loading: false,
            error: null,
          });
        }
        return;
      }
      try {
        const r = await axios.get(`${API_URL}/api/chantiers/${chantierId}/my-permissions`, { withCredentials: true });
        cache.set(chantierId, { fetchedAt: Date.now(), payload: r.data });
        if (aliveRef.current) {
          setState({
            perms: { ...DEFAULT_PERMS, ...(r.data.permissions || {}) },
            role_projet: r.data.role_projet || null,
            isOwner: !!r.data.is_owner,
            loading: false,
            error: null,
          });
        }
      } catch (e) {
        if (aliveRef.current) {
          setState({
            perms: DEFAULT_PERMS,
            role_projet: null,
            isOwner: false,
            loading: false,
            error: e?.response?.status === 403 ? "forbidden" : "error",
          });
        }
      }
    },
    [chantierId]
  );

  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
    };
  }, [load]);

  const refresh = useCallback(() => {
    if (chantierId) cache.delete(chantierId);
    return load(true);
  }, [chantierId, load]);

  return { ...state, refresh };
}

export default usePermissions;
